// src/utils/serviceTypeOptions.js
//
// SINGLE SOURCE OF TRUTH for every Service Type dropdown in the app.
//
// Storage: appSettings/serviceTypes  ->  { options: string[], updatedAt }
//
// Every page that renders a <select name="serviceType"> should call
// useServiceTypes() instead of declaring its own hardcoded array. Editing the
// list in Settings -> Service Types updates every page live (onSnapshot), with
// no redeploy.
//
// IMPORTANT - the filters are NOT driven by this list.
// Colour coding, noticeServiceTypes, hiddenServiceTypes and the various
// switch (job.serviceType) blocks stay exactly as they are. This file only
// controls what the dropdowns OFFER, never what the app DOES with a value.
//
// IMPORTANT - legacy values.
// A <select> whose value is not present in its <option> list renders BLANK, and
// saving that form silently wipes the field. Old jobs can hold a service type
// that has since been removed from Settings. Always render options with
// withCurrentValue(serviceTypes, job.serviceType) so the job's existing value
// stays selectable and can never be blanked by an unrelated edit.

import { useEffect, useState } from "react";
import { doc, getDoc, onSnapshot, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebaseConfig";

export const SERVICE_TYPES_COLLECTION = "appSettings";
export const SERVICE_TYPES_DOC = "serviceTypes";

// The 12 values that were hardcoded across the app before this file existed.
// Used to seed the settings doc the first time, and as the fallback whenever
// the doc is missing or empty so no dropdown can ever render empty.
export const DEFAULT_SERVICE_TYPES = [
  "Proposal",
  "Meeting",
  "Construction",
  "Maintenance",
  "Action Items",
  "Cleaning",
  "Bulk Trash",
  "Inspection",
  "Electrical",
  "Plumbing",
  "HVAC",
  "Concrete",
];

const serviceTypesRef = () => doc(db, SERVICE_TYPES_COLLECTION, SERVICE_TYPES_DOC);

// Trim, drop blanks, drop case-insensitive duplicates, keep the order given.
export const normalizeServiceTypes = (list) => {
  if (!Array.isArray(list)) return [];

  const seen = new Set();
  const out = [];

  list.forEach((raw) => {
    const value = String(raw ?? "").trim();
    if (!value) return;

    const key = value.toLowerCase();
    if (seen.has(key)) return;

    seen.add(key);
    out.push(value);
  });

  return out;
};

// Returns the saved list, falling back to the defaults when the doc is missing
// or holds an empty array. Never returns [].
const readSnapshot = (snap) => {
  if (!snap || !snap.exists()) return DEFAULT_SERVICE_TYPES;

  const options = normalizeServiceTypes(snap.data()?.options);
  return options.length ? options : DEFAULT_SERVICE_TYPES;
};

/**
 * One-shot read. Use inside non-React code (utils, exporters, cloud-adjacent
 * helpers). React components should use useServiceTypes() instead.
 */
export const fetchServiceTypes = async () => {
  try {
    const snap = await getDoc(serviceTypesRef());
    return readSnapshot(snap);
  } catch (err) {
    console.error("fetchServiceTypes failed, using defaults:", err);
    return DEFAULT_SERVICE_TYPES;
  }
};

/**
 * Writes the list. Used by ServiceTypesAdmin only.
 * merge:true so we never clobber anything else on the settings doc.
 */
export const saveServiceTypes = async (list) => {
  const options = normalizeServiceTypes(list);

  await setDoc(
    serviceTypesRef(),
    { options, updatedAt: serverTimestamp() },
    { merge: true }
  );

  return options;
};

/**
 * Live list of service types.
 *
 *   const { serviceTypes } = useServiceTypes();
 *
 * Renders the defaults on first paint so a dropdown is never empty while the
 * snapshot is in flight, then swaps to the saved list when it arrives.
 */
export const useServiceTypes = () => {
  const [serviceTypes, setServiceTypes] = useState(DEFAULT_SERVICE_TYPES);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      serviceTypesRef(),
      (snap) => {
        setServiceTypes(readSnapshot(snap));
        setLoading(false);
      },
      (err) => {
        console.error("useServiceTypes snapshot failed, using defaults:", err);
        setServiceTypes(DEFAULT_SERVICE_TYPES);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, []);

  return { serviceTypes, loading };
};

/**
 * Guards against blanking a job that holds a service type which has since been
 * removed from Settings.
 *
 *   {withCurrentValue(serviceTypes, editedJob.serviceType).map(...)}
 *
 * If the job's current value is missing from the list it is appended, so the
 * <select> shows the real value instead of rendering blank and wiping the
 * field on the next save.
 */
export const withCurrentValue = (list, currentValue) => {
  const options = normalizeServiceTypes(list);
  const value = String(currentValue ?? "").trim();

  if (!value) return options;

  const exists = options.some(
    (option) => option.toLowerCase() === value.toLowerCase()
  );

  return exists ? options : [...options, value];
};
